import {Project} from "./project";
import {ListEntry} from "./colDef";
import {FileEntry} from "./fileEntry";
import {User} from "./user";

export enum ClientStatus {
  ARCHIVED = 0,
  ACTIVE = 1,
  PROSPECT = 2,
  PROSPECT_ON_HOLD = 3,
  PROSPECT_LOST = 4,
}

export interface Organization {
  id: number;
  canonical_name: string;
  normalized_fingerprint?: string;
  aliases_count?: number;
  market_vacancies_count?: number;
  /** Sent only when the requesting Company bridges this org. See the API guard. */
  aliases?: { id: number; raw_name: string; platform: string }[];
  /** The requesting Company's own Client row for this org, or null when untracked. */
  my_client?: { id: number; status: number } | null;
}

/** A client as /clients/lookup returns it, for pickers. */
export type ClientRef = Pick<Client, 'id' | 'name' | 'status'>;

export interface Client extends ListEntry{
  id: number,
  name: string,
  description?: string,
  matching_description?: string,
  status: ClientStatus,
  logo?: FileEntry,
  splash?: FileEntry
  address?: string,
  url?: string,
  /** Language of documents sent to this client (#1493). Only an Admin changes it. */
  document_language?: 'nl' | 'en',
  phone?: string,
  email?: string,
  assigned_projects?: Project[],
  is_tenant_root?: number,
  projects?: Project[],
  domain?: string,
  created_at?: number|Date,
  created_by?: string|User,
  updated_at?: number|Date,
  archived_at?: number|Date,
  archived_by?: string|User,
  // Bridges to the canonical Organization once Promote-to-Client runs.
  Organization_id?: number | null,
  organization?: Organization,
  // Set by ClientController::find: 'client' vs 'prospect' so the table can
  // route/render per row type.
  __row_type?: 'client' | 'prospect',
  organization_tenant_id?: number,
  // CRM classification carried on every Client row. Only prospect|client are
  // ever written; broker/end-client is a per-Opportunity role (via_Client_id /
  // for_Client_id), not a Client type. Drives the row-type badge in /clients.
  crm_type?: 'prospect' | 'client',
  crm_source?: string,
  // Real OrganizationTenant lifecycle on prospect rows (Prospects tab column).
  prospect_status?: string,
  tags?: string[],
  notes?: string
}

/** GET /clients/{into}/merge/{from}: what a merge would move (#1849). */
export interface ClientMergePreview {
    from: {id: number; name: string; Organization_id: number | null};
    /** Why the merge cannot run, or null when it can. */
    refusal: string | null;
    /** Null for a non-admin, who may not read the other client's counts. */
    summary: {projects: number; opportunities: number; contacts: number; activities: number; quotes: number; total: number} | null;
}
